import ModalWrapper from '../../components/modals/ModalWrapper';
import { ReactElement, useMemo, useRef, useState } from 'react';
import { PaymentMethod } from '../../../../services/exports/Constants';
import PaymentManager from '../../../../services/api/PaymentManager';
import { StoreInterface } from '../../../../store/types';
import { useSelector } from 'react-redux';
import { loadStripeIfSecure, resolveStripeInit } from '../../../../utils/stripe';
import * as stripeJs from '@stripe/stripe-js';
import theme from '../../../../components-library/Theme';
import Content from './Content';
import { Elements } from '@stripe/react-stripe-js';
import { IModal } from '../../router/ModalRouter';
import { IonSkeletonText } from '@ionic/react';
import ModalHeader from '../../components/modals/ModalHeader';
import { ImperativeAddCard } from './Content';
import useEffectOnce from '../../common/hooks/useEffectOnce';
import { useTranslation } from 'react-i18next';

const AddCreditCardModal = (props: Partial<IModal>): ReactElement => {
  const { t } = useTranslation(null, {
    keyPrefix:
      'Modules:Mobile:Features:Profile:Components:ProfileModal:NestedModals:PaymentMethods:AddCreditCard:AddCreditCardModal',
  });
  const { goBack } = props;
  const ref = useRef<ImperativeAddCard>(null);
  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  // The SetupIntent lives where the restaurant's charges are made: the platform for destination charges.
  const { stripeAccount } = resolveStripeInit(company);

  const stripePromise = useMemo<null | Promise<stripeJs.Stripe>>(
    () =>
      !company?.service_provider?.stripe_key
        ? null
        : loadStripeIfSecure(company?.service_provider.stripe_key, stripeAccount ? { stripeAccount } : undefined),
    [company?.service_provider?.stripe_key, stripeAccount],
  );

  const createPaymentSetupIntent = async (): Promise<void> => {
    const { response, success } = await PaymentManager.createPaymentSetupIntent({
      company_id: company?.id,
      payment_methods: [PaymentMethod.Card],
    });

    if (success) {
      setClientSecret(response.data.client_secret);
    }
  };

  useEffectOnce(() => {
    createPaymentSetupIntent();
  });

  const addCard = async (): Promise<void> => {
    setSaving(true);
    await ref.current?.addCard();
    setSaving(false);
  };

  return (
    <ModalWrapper className="mt-4 h-[90dvh]">
      <ModalHeader
        title={t('title')}
        onBack={() => goBack()}
        endText={t('buttons.save')}
        loading={saving}
        onEndClick={addCard}
      />
      {clientSecret && stripePromise ? (
        <Elements
          key={`company-${company?.id}-credit-card-${clientSecret}`}
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: {
              variables: {
                colorText: theme.color.black.DEFAULT,
              },
            },
          }}
        >
          <Content ref={ref} {...props} />
        </Elements>
      ) : (
        <div className="flex flex-col gap-4 p-sm">
          <IonSkeletonText animated={true} className="w-full h-6" />
          <IonSkeletonText animated={true} className="w-full h-6" />
          <IonSkeletonText animated={true} className="w-full h-6" />
          <IonSkeletonText animated={true} className="w-full h-6" />
          <IonSkeletonText animated={true} className="w-full h-6" />
        </div>
      )}
    </ModalWrapper>
  );
};

export default AddCreditCardModal;
